import React, { useEffect, useCallback, useState, forwardRef, useImperativeHandle, useRef, Ref } from 'react'
import debounce from 'lodash.debounce'
import DragHandle from '@tiptap-pro/extension-drag-handle-react'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import { useSocketContextData } from 'src/context/socket'
import {
  Tooltip,
  ScrollShadow,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Input,
  Textarea,
  Image,
} from '@nextui-org/react'
import Mixpanel from 'src/lib/mixpanel'

import { Icon } from '@iconify/react'

import './editor-styles.css'
import { createSuggestions } from 'src/pages/powerdoc-view/extensions/SlashPlugin'
import { createExtensions } from 'src/pages/powerdoc-view/extensions/extensions'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import ReactDOM from 'react-dom/client'
import QuestionsContainer from 'src/pages/powerdoc-view/editor/QuestionsContainer'
import DocMenuBar from 'src/pages/powerdoc-view/editor/components/DocMenuBar'
import CoverPage from 'src/pages/powerdoc-view/editor/components/CoverPage/CoverPage'
import { useWorkspaceContextData } from 'src/context/workspace'
import { AddMarkStep } from '@tiptap/pm/transform'
import { Editor } from '@tiptap/core'
import Cookie from 'js-cookie'
import { PowerDocsEventsName } from '@/types/SocketEvents'
import AddImageModal from './components/AddImageModal'
import TableOfContentPage from './components/TableOfContent'
import CoverPageModel1 from './components/CoverPage/Designs/Model1'
import CoverPageModel4 from './components/CoverPage/Designs/Model4'
import CoverPageModel3 from './components/CoverPage/Designs/Model3'
import CoverPageModel2 from './components/CoverPage/Designs/Model2'
import TOCModel1 from './components/TableOfContent/Designs/Model1'
let cache: any
let cacheLength = -1

interface Comment {
  id: number
  uuid: string
  text: string
  username: string
  documentId: string
  isResolved: boolean
  createdAt: string
}

// eslint-disable-next-line react/display-name
export const TipTapEditor = forwardRef(
  (
    {
      currentUserWithPermission,
      markdown,
      documentUUID,
      isReadOnly,
      docDesignSettings,
      doc,
      onQuestionAnswer,
      variant,
      setDoc,
      loadDocument,
    }: {
      currentUserWithPermission: {
        sections: any[]
        canEditTheWholeDocument: boolean
      }
      markdown: string
      documentUUID?: string
      isReadOnly?: boolean
      docDesignSettings: {
        fontType: string
        fontSize: number
        sectionSpacing: number
        skipUpdate?: boolean
      }
      doc: any
      onQuestionAnswer?: (answer: string[], questionIndex: number) => Promise<void>
      variant: 'quotation' | 'proposal' | 'offer' | 'custom'
      setDoc: (doc: any) => void
      loadDocument: () => void
    },
    ref,
  ) => {
    const [tableContent, setTableContent] = useState<any[]>([])
    const [showCoverPageEdit, setShowCoverPageEdit] = useState<boolean>(false)
    const [showTableOfContentEdit, setShowTableOfContentEdit] = useState<boolean>(false)
    const [imageToEdit, setImageToEdit] = useState<any>(null)
    const [commentText, setCommentText] = useState<string>('')
    const [displayName, setDisplayName] = useState<string>('')
    const [showDisplayName, setShowDisplayName] = useState<boolean>(false)
    const [showComment, setShowComment] = useState<boolean>(false)
    const [selectedComment, setSelectedComment] = useState<any>()
    const { isOpen: isMediaModalOpen, onOpen: onOpenMediaModal, onOpenChange: onOpenChangeMediaModal } = useDisclosure()
    const suggestions = createSuggestions({ onOpenMediaModal })
    const onCommentClicked = (commentId: string, rect: any) => {
      const comment = doc?.comments?.filter((item: any) => item.uuid === commentId)?.pop()
      if (comment) {
        setShowComment(true)
        setSelectedComment({ ...comment })
        const element = document.getElementById('comment-section')
        const tiptap = document.getElementsByClassName('tiptap')?.[0]?.getBoundingClientRect()
        if (element) {
          element.style.left = `${tiptap.left + tiptap.width}px`
          element.style.top = `${rect.top + window.scrollY}px`
        }
      }
    }
    const extensions = createExtensions({
      currentUserWithPermission,
      setTableContent,
      suggestions,
      onCommentClicked,
      setShowComment,
      isReadOnly,
    })
    const {
      isOpen: isCommentModalOpen,
      onOpen: setIsCommentModalOpen,
      onClose: closeCommentModal,
      onOpenChange: commentModalOpenChange,
    } = useDisclosure()

    const [saveMessage, setSaveMessage] = useState<{ text: string; visible: boolean }>({
      text: '',
      visible: false,
    })
    const { profile } = useWorkspaceContextData()
    useEffect(() => {
      if (profile?.firstname || profile?.lastname) {
        setDisplayName(`${profile.firstname ?? ''} ${profile.lastname ?? ''}`)
      } else {
        const name = Cookie.get('displayName')
        if (name) {
          setDisplayName(name)
        } else {
          setShowDisplayName(true)
        }
      }
    }, [profile, isCommentModalOpen])

    const updateDoc = async (editor: Editor, isItComment: boolean) => {
      if (documentUUID && (!isReadOnly || isItComment)) {
        setSaveMessage({ text: 'Saving document...', visible: true })
        const content = editor.storage.markdown.getMarkdown()
        await io.emitWithAck(PowerDocsEventsName.UPDATE_POWERDOC_CONTENT, {
          content,
          variant: window.variant,
          documentUUID,
          token: localStorage.getItem('accessToken'),
          isItComment,
        })
        setSaveMessage({ text: 'Document saved', visible: true })
        setTimeout(() => {
          setSaveMessage((prev) => ({ ...prev, visible: false }))
        }, 500)
      }
    }
    const debouncedUpdate = useCallback(
      debounce(updateDoc, 500), // Delay of 500ms
      [],
    )

    useEffect(() => {
      window.variant = variant
    }, [variant])
    const getNewComment = async (comment: string, username: string) => {
      const res = await io.emitWithAck('create_comment', {
        username,
        comment,
        token: localStorage.getItem('accessToken'),
        documentUUID: doc?.uuid,
        variant,
      })
      if (!res.uuid) return null
      return res as unknown as Comment
    }

    const sandboxEditor = useEditor({
      content: '', // empty
      extensions, // same as real editor
    })

    const editor = useEditor({
      extensions,
      content: markdown,
      onUpdate: ({ editor, transaction }) => {
        console.log('isRead only ', isReadOnly)
        if (isReadOnly) {
          if (!transaction.steps.some((step) => !(step instanceof AddMarkStep))) {
            updateDoc(editor, true)
          }
        } else {
          debouncedUpdate(editor, false)
        }
      },
      editorProps: {
        decorations: (state) => {
          const { doc: tiptapDoc, tr } = state
          const decorations: Decoration[] = []
          let questionAreas = 0
          tiptapDoc.descendants((node, pos) => {
            if (node?.type?.name === 'heading' && node?.attrs?.level === 2) {
              const headingText = node.textContent.trim()
              const questions = doc.questions?.filter(
                (item: any) => item?.chapter?.trim().toLowerCase() === headingText.toLowerCase(),
              )
              if (questions?.length > 0) {
                questionAreas++
              }
            }
          })
          if (cache?.filter((item: any) => item).length === cacheLength && questionAreas === cacheLength) {
            return DecorationSet.create(tiptapDoc, cache)
          }
          tiptapDoc.descendants((node, pos) => {
            if (node?.type?.name === 'heading' && node.attrs.level === 2) {
              const headingText = node.textContent.trim()
              const questions = doc.questions
                ?.map((item: any, index: number) => ({ ...item, index }))
                ?.filter((item: any) => item?.chapter?.trim().toLowerCase() === headingText.toLowerCase())
              if (questions?.length > 0) {
                decorations.push(
                  Decoration.widget(pos, () => {
                    const container = document.createElement('div')
                    container.style.position = 'relative' // Ensure it's positioned relative to the heading

                    // Create the helper text element
                    const helperDiv = document.createElement('div')
                    const helperTextContainer = (
                      <QuestionsContainer questions={questions} onQuestionAnswer={onQuestionAnswer} />
                    )
                    const root = ReactDOM.createRoot(helperDiv)
                    root.render(helperTextContainer)

                    // Append the helper text above the heading
                    container.appendChild(helperDiv)
                    return container
                  }),
                )
              }
            }
          })
          cache = decorations
          cacheLength = decorations.filter((item) => item).length
          return DecorationSet.create(tiptapDoc, decorations)
        },
      },
    })
    const [modalName, setModalName] = useState<string>('')
    const {
      isOpen: isNameModalOpen,
      onOpenChange: onNameModalOpenChange,
      onOpen: onNameModalOpen,
      onClose: onNameModalClose,
    } = useDisclosure()
    useEffect(() => {
      const cookieName = Cookie.get('displayName')
      if (isReadOnly && !cookieName && !profile?.uuid) {
        onNameModalOpen()
      }
    }, [isReadOnly, profile])
    useEffect(() => {
      if (!editor) return

      // Define base class
      const baseClass = 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none tiptap'

      // Append dynamic classes
      const fontSizeClass =
        docDesignSettings?.fontSize === 1 ? 'small' : docDesignSettings?.fontSize === 2 ? 'medium' : 'large'
      const spacingClass = `spacing-${docDesignSettings?.sectionSpacing}`

      // Ensure classes are correctly concatenated
      const finalClass = `${baseClass} ${fontSizeClass} ${spacingClass}`.trim()

      editor.setOptions({
        editorProps: {
          attributes: {
            class: finalClass,
            style: `font-family: "${docDesignSettings?.fontType}", sans-serif;`,
          },
        },
      })
    }, [docDesignSettings, editor])

    useEffect(() => {
      if (editor) {
        //debouncedContentUpdate(editor, markdown)
        queueMicrotask(() => {
          try {
            editor.commands.setContent(markdown)
          } catch (error) {
            console.error('Broken markdown content', error)
          }
        })
      }
    }, [markdown, editor, sandboxEditor])
    useEffect(() => {
      if (isReadOnly) {
        const element = document.getElementsByClassName('tiptap')?.[0]
        if (element) {
          element.addEventListener('keydown', (e) => e.preventDefault())
        }
      }
    }, [editor, isReadOnly])
    useEffect(() => {
      return () => {
        if (editor) {
          editor.destroy() // Clean up the editor on unmount
        }
        // Cancel the debounce on unmount to prevent memory leaks
        debouncedUpdate.cancel()
      }
    }, [editor, debouncedUpdate])

    const { io } = useSocketContextData()

    useImperativeHandle(
      ref,
      () => {
        return {
          getMarkdown: () => {
            return editor?.storage.markdown.getMarkdown()
          },
          setMarkdown: (content: string) => {
            editor?.commands?.setContent(content)
          },
          unsetComment: (commentUUID: string) => {
            editor?.commands?.unsetComment(commentUUID)
          },
        }
      },
      [editor],
    )
    useEffect(() => {
      if (onOpenMediaModal) {
        //@ts-ignore
        window.documentEditImage = (imageRef: Ref<HTMLElement | null>) => {
          setImageToEdit(imageRef)
          onOpenMediaModal()
        }
      }
    }, [onOpenMediaModal])

    const scrollToItem = (id: string) => {
      const headingElement = document.getElementById(id)
      if (headingElement) {
        headingElement.scrollIntoView({ behavior: 'smooth' })

        // Extract text from <strong> element if it exists
        const strongElement = headingElement.querySelector('strong')
        const headingText = headingElement ? headingElement.textContent : 'Unknown'

        // Track the scroll event for Table of Content navigation
        Mixpanel.track('Table of content item clicked', {
          heading_text: headingText,
          element_id: id,
        })

        headingElement.classList.add('highlight')
        setTimeout(() => {
          headingElement.classList.remove('highlight') // Remove highlight after 2 seconds
        }, 2000)
      }
    }

    const activeNodePos = useRef<number>(-1)
    const createNewBlock = () => {
      if (!editor) return
      const { state, view } = editor
      const transaction = state.tr.insert(activeNodePos.current, state.schema.nodes.paragraph.create())
      transaction.insertText('', activeNodePos.current + 1)
      view.dispatch(transaction)
      editor
        .chain()
        .focus()
        .setTextSelection(activeNodePos.current + 1)
        .run()
    }
    const onNodeChange = useCallback((props) => {
      const { node, pos } = props
      if (node) {
        activeNodePos.current = pos + node.nodeSize
      } else {
        activeNodePos.current = -1
      }
    }, [])

    const addNewComment = async () => {
      const newComment = await getNewComment(commentText, displayName)
      if (newComment) {
        editor?.commands.setComment(newComment.uuid)
        closeCommentModal()
        Cookie.set('displayName', displayName)
        setShowDisplayName(false)
        setDoc((prev: any) => {
          switch (variant.toUpperCase()) {
            case 'PROPOSAL':
              return {
                ...prev,
                proposal: {
                  ...prev.proposal,
                  comments: [newComment, ...(prev.proposal?.comments ?? [])],
                },
              }
            case 'QUOTATION':
              return {
                ...prev,
                quotation: {
                  ...prev.quotation,
                  comments: [newComment, ...(prev.quotation?.comments ?? [])],
                },
              }
            case 'OFFER':
              return {
                ...prev,
                offer: {
                  ...prev.offer,
                  comments: [newComment, ...(prev.offer?.comments ?? [])],
                },
              }
          }
          return prev
        })
      }
    }
    const resolveComment = async () => {
      if (selectedComment) {
        const commentUUID = selectedComment.uuid
        editor?.commands.unsetComment(commentUUID)
        setShowComment(false)
        io.emit('resolve_comment', {
          token: localStorage.getItem('accessToken'),
          documentUUID: doc?.uuid,
          commentUUID,
        })
        setDoc((prev: any) => {
          switch (variant.toUpperCase()) {
            case 'PROPOSAL':
              return {
                ...prev,
                proposal: {
                  ...prev.proposal,
                  comments: (prev.proposal?.comments ?? []).map((comment: any) => {
                    if (comment.uuid === commentUUID) {
                      return {
                        ...comment,
                        isResolved: true,
                      }
                    } else {
                      return comment
                    }
                  }),
                },
              }
            case 'QUOTATION':
              return {
                ...prev,
                quotation: {
                  ...prev.quotation,
                  comments: (prev.quotation?.comments ?? []).map((comment: any) => {
                    if (comment.uuid === commentUUID) {
                      return {
                        ...comment,
                        isResolved: true,
                      }
                    } else {
                      return comment
                    }
                  }),
                },
              }
            case 'OFFER':
              return {
                ...prev,
                offer: {
                  ...prev.offer,
                  comments: (prev.offer?.comments ?? []).map((comment: any) => {
                    if (comment.uuid === commentUUID) {
                      return {
                        ...comment,
                        isResolved: true,
                      }
                    } else {
                      return comment
                    }
                  }),
                },
              }
          }
          return prev
        })
        setSelectedComment({})
      }
    }
    useEffect(() => {
      if (editor) {
        const elements = document.querySelectorAll('h2[data-permission-id]')
        const alreadyViewed: string[] = []
        let debounceTimeout: NodeJS.Timeout | null = null
        editor.commands.cleanInvalidMedia()
        const emitViewedSections = () => {
          if (alreadyViewed.length > 0 && displayName && isReadOnly) {
            // 👇 Your socket emit logic here
            io?.emitWithAck(PowerDocsEventsName.TRACK_SECTION_VIEW, {
              sectionViewed: alreadyViewed,
              variant,
              displayName,
              documentUUID,
            })
          }
        }

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const permissionId = entry.target.getAttribute('data-permission-id')
                if (permissionId && !alreadyViewed.includes(permissionId)) {
                  alreadyViewed.push(permissionId)

                  // 🔁 Debounce: wait 500ms of no new triggers before emitting
                  if (debounceTimeout) clearTimeout(debounceTimeout)
                  debounceTimeout = setTimeout(() => {
                    emitViewedSections()
                  }, 500)
                }
              }
            })
          },
          {
            threshold: 0.1,
          },
        )

        elements.forEach((el) => observer.observe(el))

        return () => {
          observer.disconnect()
          if (debounceTimeout) clearTimeout(debounceTimeout)
        }
      }
    }, [editor, displayName, isReadOnly])

    if (!editor) {
      return null
    }
    return (
      <>
        {!isReadOnly && (
          <DocMenuBar
            editor={editor}
            content={markdown}
            documentUUID={documentUUID}
            saveMessage={saveMessage}
            doc={doc}
            activePage={variant}
          />
        )}
        {!isReadOnly && (
          <CoverPage
            doc={doc?.[variant.toLowerCase()]}
            isVisible={showCoverPageEdit}
            close={() => setShowCoverPageEdit(false)}
            variant={variant}
          />
        )}
        {!isReadOnly && (
          <TableOfContentPage
            doc={doc?.[variant.toLowerCase()]}
            isVisible={showTableOfContentEdit}
            close={() => setShowTableOfContentEdit(false)}
            loadDocument={loadDocument}
            setDoc={setDoc}
            variant={variant}
          />
        )}
        {!isReadOnly && (
          <div className="pb-5">
            <div className="relative w-[794px] mx-auto mt-20 mb-2 z-[30]">
              <div
                className="flex items-center justify-center text-white text-md font-semibold cursor-pointer rounded-xl bg-[#58A1B1] px-6 py-2 gap-2"
                onClick={() => {
                  setShowCoverPageEdit(true)
                  Mixpanel.track('Cover Page Edit Clicked', {
                    document_id: documentUUID,
                    user_action: 'Opened Cover Page Editor',
                    timestamp: new Date().toISOString(),
                  })
                }}
              >
                EDIT COVER PAGE + LOGO <Icon icon={'lucide:edit'} width="20" />
              </div>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 w-[50px]">
                <Image src="/img/editor/avatar-cover-page.png" className="w-full h-full" radius="none" />
              </div>
            </div>
            <div className="relative w-[794px] mx-auto mt-10 mb-2 z-[30]">
              <div
                className="flex items-center justify-center text-white text-md font-semibold cursor-pointer rounded-xl bg-[#58A1B1] px-6 py-2 gap-2"
                onClick={() => {
                  setShowTableOfContentEdit(true)
                  Mixpanel.track('Table of content Edit Clicked', {
                    document_id: documentUUID,
                    user_action: 'Opened Cover Page Editor',
                    timestamp: new Date().toISOString(),
                  })
                }}
              >
                EDIT TABLE OF CONTENT <Icon icon={'lucide:edit'} width="20" />
              </div>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 w-[50px]">
                <Image src="/img/editor/avatar-table-of-content.png" className="w-full h-full" radius="none" />
              </div>
            </div>
          </div>
        )}
        {isReadOnly && doc && (
          <div
            className={'mt-2 max-w-full h-[260mm] w-[794px] rounded-xl p-8 border '}
            style={{
              padding: '0.5in',
              borderColor: 'none',
            }}
          >
            {(() => {
              switch (doc[variant.toLowerCase()]?.coverPageDesign) {
                case 1:
                default:
                  return (
                    <CoverPageModel1
                      title={doc[variant.toLowerCase()]?.title ?? doc.title}
                      variant={variant}
                      documentDate={doc[variant.toLowerCase()]?.documentDate}
                      proposedTo={doc[variant.toLowerCase()]?.proposedTo}
                      createdBy={doc[variant.toLowerCase()]?.createdBy}
                      logo={doc[variant.toLowerCase()]?.coverLogo}
                      documentProjectID={doc[variant.toLowerCase()]?.documentProjectID}
                    />
                  )
                case 2:
                  return (
                    <CoverPageModel2
                      title={doc[variant.toLowerCase()]?.title ?? doc.title}
                      variant={variant}
                      documentDate={doc[variant.toLowerCase()]?.documentDate}
                      proposedTo={doc[variant.toLowerCase()]?.proposedTo}
                      createdBy={doc[variant.toLowerCase()]?.createdBy}
                      logo={doc[variant.toLowerCase()]?.coverLogo}
                      documentProjectID={doc[variant.toLowerCase()]?.documentProjectID}
                    />
                  )
                case 3:
                  return (
                    <CoverPageModel3
                      title={doc[variant.toLowerCase()]?.title ?? doc.title}
                      variant={variant}
                      documentDate={doc[variant.toLowerCase()]?.documentDate}
                      proposedTo={doc[variant.toLowerCase()]?.proposedTo}
                      createdBy={doc[variant.toLowerCase()]?.createdBy}
                      logo={doc[variant.toLowerCase()]?.coverLogo}
                      documentProjectID={doc[variant.toLowerCase()]?.documentProjectID}
                    />
                  )
                case 4:
                  return (
                    <CoverPageModel4
                      title={doc[variant.toLowerCase()]?.title ?? doc.title}
                      variant={variant}
                      documentDate={doc[variant.toLowerCase()]?.documentDate}
                      proposedTo={doc[variant.toLowerCase()]?.proposedTo}
                      createdBy={doc[variant.toLowerCase()]?.createdBy}
                      logo={doc[variant.toLowerCase()]?.coverLogo}
                      documentProjectID={doc[variant.toLowerCase()]?.documentProjectID}
                    />
                  )
              }
            })()}
          </div>
        )}
        {isReadOnly && doc && (
          <div
            className={'mt-2 max-w-full h-[260mm] w-[794px] rounded-xl p-8 border '}
            style={{
              padding: '0.5in',
              borderColor: 'none',
            }}
          >
            <TOCModel1 doc={doc[variant.toLowerCase()]} />
          </div>
        )}
        {/* <div className={'flex max-w-full w-[794px] border rounded-xl px-2 md:px-20 py-2 md:py-8 my-[20px]'}> */}
        <div className={'flex max-w-full w-[794px] border rounded-xl p-[0.6in]'}>
          <div className={'toc-wrapper'}>
            <div className={'toc'}>
              <div className={'toc-mini p-2'}>
                {tableContent
                  .filter((item) => item.level < 3)
                  .map((item) => (
                    <div key={item.label} className="toc-item-mini toc-light">
                      ▃▃
                    </div>
                  ))}
              </div>
              <div className="toc-list flex flex-col shadow-md rounded-xl h-[300px] p-4 text-default overflow-y-scroll bg-white w-[250px] scrollbar-hide opacity-0 transition-opacity duration-300 hover:opacity-100 hover:block hidden">
                <ScrollShadow className="mb-3" hideScrollBar size={40}>
                  {tableContent.map((item, index) => (
                    <div
                      onClick={() => scrollToItem(item.id)}
                      title={item.textContent}
                      className="toc-item cursor-pointer text-default-700 text-xs p-2 rounded-lg hover:bg-gray-100 truncate"
                      key={item.id || index}
                    >
                      {item.textContent}
                    </div>
                  ))}
                </ScrollShadow>
              </div>
            </div>
          </div>

          <div
            id={'comment-section'}
            className={`absolute bg-white z-50 w-[200px] border border-gray-200 rounded-xl shadow-sm hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 ${
              showComment ? '' : 'hidden'
            }`}
          >
            {showComment && (
              <div className={'bg-white p-4'}>
                <p className={'text-xs text-default-500'}>{selectedComment?.username}</p>
                <p className={'text-sm'}>{selectedComment?.text}</p>
                <div className={'flex flex-row justify-end'}>
                  <Button variant={'solid'} color="secondary" size={'sm'} onPress={resolveComment}>
                    <Icon icon={'lucide:check-check'} color={'#fff'} />
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className={'tiptap relative'}>
            {doc[variant.toLowerCase()]?.isStillGenerating && (
              <div className="flex z-50 absolute top-0 left-0 w-full h-full transparent"></div>
            )}
            <EditorContent editor={editor} content={markdown} variant={variant} />

            {editor && !isReadOnly && (
              <DragHandle
                editor={editor}
                tippyOptions={{
                  placement: 'left-start',
                  interactive: true,
                  delay: [0, 5000], // Add more time before hiding
                  hideOnClick: false,
                  appendTo: 'parent', // Ensures tooltip is appended to the relevant container
                  onMount(instance) {
                    // Prevent tooltip from hiding accidentally
                    instance.popper.style.pointerEvents = 'auto'
                  },
                  onHide(instance) {
                    // Cleanup to allow proper hiding
                    instance.popper.style.pointerEvents = ''
                  },
                  popperOptions: {
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport', // Ensures tooltip stays visible
                        },
                      },
                    ],
                  },
                }}
                onNodeChange={onNodeChange}
                className="w-[50px] absolute -left-[40px]"
              >
                <div className={'flex p-0 items-top z-50'}>
                  <Tooltip
                    content="Add section below"
                    color="foreground"
                    placement="bottom"
                    offset={5}
                    size="sm"
                    radius="sm"
                  >
                    <div className="flex w-[25px] cursor-pointer text-default-300" onClick={() => createNewBlock()}>
                      <Icon icon="tabler:plus" width="20" />
                    </div>
                  </Tooltip>
                  <Tooltip
                    content="Drag to move"
                    color="foreground"
                    placement="bottom"
                    offset={5}
                    size="sm"
                    radius="sm"
                  >
                    <div className="flex w-[25px] cursor-grab text-default-300">
                      <Icon icon="tabler:grip-vertical" width="20" />
                    </div>
                  </Tooltip>
                </div>
              </DragHandle>
            )}
            <AddImageModal
              isMediaModalOpen={isMediaModalOpen}
              onOpenChangeMediaModal={onOpenChangeMediaModal}
              imageToEdit={imageToEdit}
              setImageToEdit={setImageToEdit}
              editor={editor}
            />
            <BubbleMenu
              id={'tippy-tap'}
              editor={editor}
              className="z-0 p-1 border rounded-lg border-slate-400 bg-white"
              tippyOptions={{}}
            >
              <button
                className="rounded-md bg-white/10 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-white/20"
                onClick={setIsCommentModalOpen}
              >
                <Icon icon={'lucide:message-circle-more'} color={'#1c1c1c'} />
              </button>
            </BubbleMenu>
            <Modal isOpen={isCommentModalOpen} onOpenChange={commentModalOpenChange} size={'4xl'}>
              <ModalContent>
                {(onClose) => (
                  <>
                    <ModalHeader className="flex flex-col gap-1">Add New Comment</ModalHeader>
                    <ModalBody>
                      <div className={'flex flex-col gap-4 '}>
                        <div className={'flex flex-col justify-between gap-4'}>
                          {showDisplayName && (
                            <Input
                              autoFocus
                              variant="bordered"
                              labelPlacement="outside"
                              label={'Display Name'}
                              placeholder="Your display name e.g John Doe"
                              className="w-full"
                              onChange={(e) => setDisplayName(e.target.value)}
                            />
                          )}

                          <Textarea
                            variant={'bordered'}
                            label={'Content'}
                            labelPlacement="outside"
                            placeholder={'Insert your comment here. e.g Make sure to make this part smoother,..'}
                            onChange={(e) => setCommentText(e.target.value)}
                            minRows={5}
                            maxRows={5}
                          />
                        </div>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="secondary"
                        onPress={() => {
                          addNewComment()
                        }}
                      >
                        Insert Comment
                      </Button>
                    </ModalFooter>
                  </>
                )}
              </ModalContent>
            </Modal>
            <Modal isOpen={isNameModalOpen} onOpenChange={onNameModalOpenChange} size={'4xl'}>
              <ModalContent>
                {(onClose) => (
                  <>
                    <ModalHeader className="flex flex-col gap-1">Setup Your Name</ModalHeader>
                    <ModalBody>
                      <div className={'flex flex-col gap-4 '}>
                        <div className={'flex flex-col justify-between gap-4'}>
                          <Input
                            autoFocus
                            variant="bordered"
                            labelPlacement="outside"
                            label={'Display Name'}
                            placeholder="Your display name e.g John Doe"
                            className="w-full"
                            onKeyPress={(e) => {
                              console.log(e)
                              if (e.key === 'Enter') {
                                setDisplayName(modalName)
                                Cookie.set('displayName', modalName)
                                onClose()
                              }
                            }}
                            onChange={(e) => setModalName(e.target.value)}
                          />
                        </div>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="secondary"
                        onPress={() => {
                          setDisplayName(modalName)
                          Cookie.set('displayName', modalName)
                          onClose()
                        }}
                      >
                        Save
                      </Button>
                    </ModalFooter>
                  </>
                )}
              </ModalContent>
            </Modal>
          </div>
        </div>
      </>
    )
  },
)
